import type { z } from 'zod';
import { decodeClassFromDb, encodeClassToDb } from '../classes/codecs';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { decodeLessonEnrolmentFromDb, encodeLessonEnrolmentToDb } from '../lessonEnrolments/codecs';
import { buildRecordId } from '../recordIds';
import { DBStudentActivityLogSchema, StudentActivityLogSchema } from './schemas';

type DBStudentActivityLog = z.infer<typeof DBStudentActivityLogSchema>;
type StudentActivityLog = z.infer<typeof StudentActivityLogSchema>;

/** The teacher storage holds for an entry with none, since it keys an index. */
const UNASSIGNED_ACTIVITY_LOG_TEACHER = '_';

export const StudentActivityLogCodecConfig: TransformerConfig<
  typeof StudentActivityLogSchema,
  typeof DBStudentActivityLogSchema,
  'id' | 'classId'
> = {
  name: 'StudentActivityLog',
  clientSchema: StudentActivityLogSchema,
  dbSchema: DBStudentActivityLogSchema,
  transformers: [
    createRenameTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>(
      'studentId',
      'student'
    ),
    createRenameTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>(
      'createdBy',
      'actor'
    ),
    createRenameTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>(
      'teacherId',
      'teacher',
      { absentAs: UNASSIGNED_ACTIVITY_LOG_TEACHER, alwaysStored: true }
    ),
    createRenameTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>(
      'classCode',
      'classId'
    ),
    createRenameTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>(
      'recentAttendedTeacherIds',
      'recentAttendedTeachers'
    ),

    // activityType + creationDatetime → typeDatetimeIdentifier: 'activityType#creationDatetime'
    // Note: activityType only exists in client schema, creationDatetime exists in both
    createTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>({
      encoder: {
        sourceFields: ['activityType', 'creationDatetime'] as const,
        dependencies: ['activityType', 'creationDatetime'] as const,
        encode: ({ activityType, creationDatetime }) => ({
          typeDatetimeIdentifier: `${activityType}#${creationDatetime}`,
          creationDatetime, // Only pass through fields that exist in DB schema
        }),
      },
      decoder: {
        sourceFields: ['typeDatetimeIdentifier'] as const,
        decode: ({ typeDatetimeIdentifier }) => {
          const [activityType, creationDatetime] = (
            typeDatetimeIdentifier as DBStudentActivityLog['typeDatetimeIdentifier']
          ).split('#');
          return {
            activityType: activityType as StudentActivityLog['activityType'],
            creationDatetime: Number(creationDatetime),
          };
        },
      },
    }),
    // class: the class snapshot, through the class's own codec. Legacy records pre-date the
    // snapshot being written on every log entry, so the field may be absent — short-circuit both
    // directions in that case.
    createTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>({
      encoder: {
        sourceFields: ['class'] as const,
        encode: ({ class: classData }) => {
          if (classData === undefined) return {};
          return {
            class: encodeClassToDb({
              data: classData as NonNullable<StudentActivityLog['class']>,
            }),
          };
        },
      },
      decoder: {
        sourceFields: ['class'] as const,
        decode: ({ class: classData }) => {
          if (classData === undefined) return {};
          return {
            class: decodeClassFromDb({
              data: classData as NonNullable<DBStudentActivityLog['class']>,
            }),
          };
        },
      },
    }),
    // recentAttendedLessons: the enrolment snapshots, each through the enrolment's own codec
    createTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>({
      encoder: {
        sourceFields: ['recentAttendedLessons'] as const,
        encode: ({ recentAttendedLessons }) => {
          if (!recentAttendedLessons) return {};
          return {
            recentAttendedLessons: (
              recentAttendedLessons as NonNullable<StudentActivityLog['recentAttendedLessons']>
            ).map(enrolment => encodeLessonEnrolmentToDb({ data: enrolment })),
          };
        },
      },
      decoder: {
        sourceFields: ['recentAttendedLessons'] as const,
        decode: ({ recentAttendedLessons }) => {
          if (!recentAttendedLessons) return {};
          return {
            recentAttendedLessons: (
              recentAttendedLessons as NonNullable<DBStudentActivityLog['recentAttendedLessons']>
            ).map(enrolment => decodeLessonEnrolmentFromDb({ data: enrolment })),
          };
        },
      },
    }),
  ],
  derived: {
    id: entry => buildRecordId('activityLogEntry', entry),
    classId: entry => buildRecordId('class', { subject: entry.subject, code: entry.classCode }),
  },
};

export const encodeStudentActivityLogToDb = createEncodeToDbFn(StudentActivityLogCodecConfig);
export const decodeStudentActivityLogFromDb = createDecodeFromDbFn(StudentActivityLogCodecConfig);
